import React, { useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { extractVariables } from './templateVariables';

// Base URL shown in the curl examples. Same-origin deploys have no
// REACT_APP_SERVER_URL, so fall back to where the dashboard is served from.
const API_BASE = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '') || window.location.origin;

function CodeBlock({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable (http) — user can still select the text */ }
  };
  return (
    <div className="code-block">
      <button className="btn btn-ghost btn-sm btn-icon copy-btn" onClick={copy} title="Copy">
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
      {code}
    </div>
  );
}

// Copy-ready curl commands for driving a template from the external API.
// `template` = { id?, name, json }; without an id (unsaved) a placeholder is
// shown so the shape is still copyable.
export default function TemplateApiModal({ template, heading, onClose }) {
  const vars = extractVariables(template?.json);
  const id = template?.id || 'TEMPLATE_ID';

  const varsBody = vars.length
    ? `{\n${vars.map((v) => `      "${v}": "value"`).join(',\n')}\n    }`
    : '{}';

  const pushCurl =
`curl -X POST ${API_BASE}/api/v1/devices/DEVICE_UUID/push \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "template_id": "${id}",
    "variables": ${varsBody}
  }'`;

  const varsCurl =
`curl -X PUT ${API_BASE}/api/v1/devices/DEVICE_UUID/variables \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{ "variables": ${varsBody} }'`;

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <h3 style={{ margin: 0, flex: 1 }}>{heading || `Use “${template?.name || 'template'}” via the API`}</h3>
          <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose} title="Close">
            <X size={16} />
          </button>
        </div>

        {!template?.id && (
          <p className="props-hint">This template isn’t saved yet — save it to get its real ID (shown here as TEMPLATE_ID).</p>
        )}

        {vars.length > 0 ? (
          <>
            <p style={{ color: 'var(--muted)', fontSize: 13, margin: '8px 0' }}>
              This template has {vars.length} variable{vars.length === 1 ? '' : 's'} — the API fills them at push time:
            </p>
            <div className="chip-row" style={{ marginBottom: 14 }}>
              {vars.map((v) => <span key={v} className="chip chip-var">{`{{${v}}}`}</span>)}
            </div>
          </>
        ) : (
          <p style={{ color: 'var(--muted)', fontSize: 13, margin: '8px 0 14px' }}>
            This template has no variables — everything on it is fixed content. Wrap any text in
            {' '}<span className="chip chip-var">{'{{name}}'}</span> to make it fillable via the API.
          </p>
        )}

        <label className="label">Show this template on a screen</label>
        <CodeBlock code={pushCurl} />

        {vars.length > 0 && (
          <>
            <label className="label">Update variables live (re-renders whatever the screen is showing)</label>
            <CodeBlock code={varsCurl} />
          </>
        )}

        <p style={{ color: 'var(--faint)', fontSize: 12, margin: '4px 0 0' }}>
          Needs an API key (set API_KEYS on the server) sent as X-API-Key or a Bearer token.
          Find device UUIDs on the Screens page.
        </p>
      </div>
    </div>
  );
}
